import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowRight, CalendarDays, MapPin, Users } from "lucide-react";
import { api } from "@/lib/api";
import { Reveal } from "@/components/ScrollReveal";

function formatDate(dateIso) {
    if (!dateIso) return "";
    const d = new Date(dateIso);
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
}

function Urgency({ seats }) {
    if (seats <= 0) {
        return <span className="rounded-full bg-white/[0.05] px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] text-white/50">Complet</span>;
    }
    if (seats <= 3) {
        return <span className="rounded-full bg-brand-orange/20 px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] text-brand-orange">Plus que {seats} places</span>;
    }
    if (seats <= 6) {
        return <span className="rounded-full bg-amber-500/15 px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] text-amber-300">{seats} places restantes</span>;
    }
    return <span className="rounded-full bg-emerald-500/15 px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] text-emerald-300">{seats} places</span>;
}

function NotifyBlock({ cursusName }) {
    const [email, setEmail] = useState("");
    const submit = (e) => {
        e.preventDefault();
        if (!email) return;
        toast.success(`Nous vous préviendrons dès qu'une session ${cursusName.replace("Cursus ", "")} sera ouverte.`);
        setEmail("");
    };
    return (
        <div className="rounded-3xl border border-white/10 bg-white/[0.03] px-6 py-12 md:px-10 md:py-16" data-testid="sessions-notify-block">
            <div className="mx-auto max-w-2xl text-center">
                <div className="font-display text-3xl leading-tight md:text-4xl">
                    Les prochaines sessions seront <em className="italic text-brand-orange">bientôt annoncées.</em>
                </div>
                <p className="mt-5 text-sm text-white/60">
                    Laissez-nous votre email pour être informé en priorité de l'ouverture des inscriptions.
                </p>
                <form onSubmit={submit} className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
                    <input
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="votre@email.fr"
                        className="h-12 w-full rounded-full border border-white/15 bg-white/[0.04] px-5 text-sm text-white placeholder:text-white/30 focus:border-brand-orange/60 focus:outline-none sm:w-72"
                        data-testid="sessions-notify-email"
                    />
                    <button
                        type="submit"
                        className="inline-flex h-12 items-center gap-2 rounded-full bg-brand-orange px-6 text-sm font-semibold text-white transition-all hover:bg-[#d9772c]"
                        data-testid="sessions-notify-submit"
                    >
                        Être notifié
                        <ArrowRight className="h-4 w-4" />
                    </button>
                </form>
            </div>
        </div>
    );
}

export default function CursusSessions({ slug, cursusName, formations = [] }) {
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const today = new Date().toISOString().slice(0, 10);

    useEffect(() => {
        let mounted = true;
        setLoading(true);
        api.get(`/sessions`, { params: { cursus: slug, from_date: today } })
            .then((r) => mounted && setSessions(r.data || []))
            .catch(() => mounted && setSessions([]))
            .finally(() => mounted && setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slug]);

    const ordered = [...formations].sort((a, b) => (a.order || 0) - (b.order || 0));

    return (
        <section
            id="sessions"
            data-testid="cursus-sessions"
            className="relative overflow-hidden bg-brand-ink py-28 text-white md:py-36"
        >
            <div className="absolute inset-0 grain pointer-events-none opacity-25" />
            <div className="relative mx-auto max-w-7xl px-6">
                <Reveal>
                    <span className="text-[11px] font-semibold uppercase tracking-[0.32em] text-brand-orange">
                        Calendrier
                    </span>
                    <h2 className="mt-5 max-w-4xl font-display font-normal leading-[0.95] tracking-tightest text-[40px] sm:text-5xl lg:text-[72px]">
                        Prochaines <em className="italic text-white/55">sessions.</em>
                    </h2>
                </Reveal>

                <div className="mt-16">
                    {loading ? (
                        <div className="grid gap-4 md:grid-cols-3">
                            {[1, 2, 3].map((i) => (
                                <div key={i} className="h-40 animate-pulse rounded-3xl bg-white/5" />
                            ))}
                        </div>
                    ) : sessions.length === 0 ? (
                        <NotifyBlock cursusName={cursusName} />
                    ) : (
                        <div className="space-y-16">
                            {ordered.map((f) => {
                                const group = sessions.filter((s) => s.formation_slug === f.slug);
                                if (!group.length) return null;
                                return (
                                    <div key={f.slug}>
                                        <div className="mb-6 flex items-end justify-between border-b border-white/10 pb-4">
                                            <div>
                                                <div className="text-[10px] uppercase tracking-[0.28em] text-brand-orange">
                                                    {f.name}
                                                </div>
                                                <div className="mt-2 font-display text-xl text-white md:text-2xl">
                                                    {group.length} session{group.length > 1 ? "s" : ""} à venir
                                                </div>
                                            </div>
                                            <Link
                                                to={`/formations/${f.slug}`}
                                                className="hidden text-xs font-semibold uppercase tracking-[0.2em] text-white/60 hover:text-white md:inline"
                                            >
                                                Programme détaillé →
                                            </Link>
                                        </div>

                                        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                                            {group.map((s) => (
                                                <motion.div
                                                    key={s.id}
                                                    initial={{ opacity: 0, y: 20 }}
                                                    whileInView={{ opacity: 1, y: 0 }}
                                                    viewport={{ once: true, margin: "-10%" }}
                                                    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                                                    className="group relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] p-6 transition-all duration-500 hover:-translate-y-1 hover:border-brand-orange/40"
                                                    data-testid={`cursus-session-${s.id}`}
                                                >
                                                    <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-brand-orange">
                                                        <CalendarDays className="h-3.5 w-3.5" />
                                                        {formatDate(s.start_date)}
                                                    </div>
                                                    <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-white/70">
                                                        <span className="inline-flex items-center gap-1.5">
                                                            <MapPin className="h-3.5 w-3.5 text-brand-orange" />
                                                            {s.city}
                                                        </span>
                                                        <span className="inline-flex items-center gap-1.5">
                                                            <Users className="h-3.5 w-3.5 text-brand-orange" />
                                                            {s.available_seats} / {s.total_seats}
                                                        </span>
                                                    </div>
                                                    <div className="mt-4">
                                                        <Urgency seats={s.available_seats} />
                                                    </div>
                                                    <div className="mt-6 flex items-center justify-between border-t border-white/10 pt-5">
                                                        <div className="text-sm">
                                                            <div className="font-semibold text-white">{s.trainer_name}</div>
                                                            <div className="text-xs text-white/45">Formateur</div>
                                                        </div>
                                                        <Link
                                                            to={`/inscription/${s.id}`}
                                                            data-testid={`cursus-session-cta-${s.id}`}
                                                            className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-4 py-2 text-xs font-semibold text-white transition-all hover:bg-[#d9772c]"
                                                        >
                                                            S'inscrire
                                                            <ArrowRight className="h-3.5 w-3.5" />
                                                        </Link>
                                                    </div>
                                                </motion.div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}
